import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BarChart3, Activity, TrendingUp } from "lucide-react";
import ReportDashboard from "./ReportDashboard";
import NasdaqReportDashboard from "./NasdaqReportDashboard";

const API_BASE = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin + '/api' : '/api');
const ICS_API_KEY = process.env.REACT_APP_ICS_API_KEY || '';

function apiHeaders() {
  const h = { 'Accept': 'application/json' };
  if (ICS_API_KEY) h['X-API-Key'] = ICS_API_KEY;
  return h;
}

function StatusBadge({ status }) {
  const variants = {
    completed: "bg-green-500/20 text-green-400 border-green-500/30",
    running: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    partial: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${variants[status] || variants.completed}`}>
      {status}
    </span>
  );
}

const TABS = [
  { id: "signals", label: "Signal Analysis", icon: Activity },
  { id: "nasdaq", label: "Nasdaq Strategy", icon: TrendingUp },
];

const PRESET_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'all_time', label: 'All Time' },
];

const OUTCOME_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'TP4 Hit', label: 'TP4 Hit' },
  { value: 'TP3 Hit', label: 'TP3 Hit' },
  { value: 'TP2 Hit', label: 'TP2 Hit' },
  { value: 'TP1 Hit', label: 'TP1 Hit' },
  { value: 'SL Hit', label: 'SL Hit' },
  { value: 'Open', label: 'Open' },
];

const STATS_PERIOD_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'daily', label: 'Daily' },
  { value: 'yearly', label: 'Yearly' },
];

const PER_PAGE_OPTIONS = [10, 15, 25, 50];

const DEFAULT_NASDAQ_FILTERS = {
  preset: 'all',
  from_date: '',
  to_date: '',
  ticker: '',
  outcome: '',
  stats_period: 'auto',
  per_page: 15,
};

export default function ReportSection() {
  const location = useLocation();
  const activeTab = location.pathname.includes("nasdaq") ? "nasdaq" : "signals";
  const market = location.pathname.includes("psx") ? "PSX" : (location.pathname.includes("tasi") ? "TASI" : "");

  const [signals, setSignals] = useState([]);
  const [signalsFilters, setSignalsFilters] = useState({ from_date: '', to_date: '' });
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState(null);

  // Nasdaq state
  const [nasdaqMode, setNasdaqMode] = useState("hourly");
  const [nasdaqData, setNasdaqData] = useState(null);
  const [nasdaqLoading, setNasdaqLoading] = useState(false);
  const [nasdaqError, setNasdaqError] = useState(null);
  const [nasdaqFilters, setNasdaqFilters] = useState({ ...DEFAULT_NASDAQ_FILTERS });

  useEffect(() => {
    setSelectedId("");
  }, [market]);

  // Fetch signals list
  useEffect(() => {
    if (activeTab !== "signals") return;
    setLoading(true);
    const url = market ? `${API_BASE}/signals?market=${market}` : `${API_BASE}/signals`;
    fetch(url, { headers: apiHeaders() })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const list = data.signals || [];
        setSignals(list);
        setLoading(false);
        if (list.length > 0 && !selectedId) {
          setSelectedId(String(list[0].id));
        }
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [activeTab, selectedId, market]);

  // Fetch signal report data
  useEffect(() => {
    if (activeTab !== "signals" || !selectedId) {
      setReportData(null);
      return;
    }
    setReportLoading(true);
    setReportData(null);

    const params = new URLSearchParams(signalsFilters);
    for (const [k, v] of [...params.entries()]) {
      if (!v) params.delete(k);
    }
      params.set('id', selectedId);
      params.set('data', 'full');
      const qs = `?${params.toString()}`;

    fetch(`${API_BASE}/signals${qs}`, { headers: apiHeaders() })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setReportData(data);
        setReportLoading(false);
      })
      .catch(() => {
        setReportLoading(false);
      });
  }, [selectedId, activeTab, signalsFilters]);

  // Fetch nasdaq data
  useEffect(() => {
    if (activeTab !== "nasdaq") return;
    setNasdaqLoading(true);
    setNasdaqData(null);
    setNasdaqError(null);

    const params = new URLSearchParams({ mode: nasdaqMode, ...nasdaqFilters });
    for (const [k, v] of [...params.entries()]) {
      if (!v) params.delete(k);
    }

    fetch(`${API_BASE}/nasdaq?${params.toString()}`, { headers: apiHeaders() })
      .then((res) => {
        if (!res.ok) {
          if (res.status === 403) throw new Error("Nasdaq data is not public yet. Enable public access in the admin panel.");
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (!data.is_public) {
          setNasdaqError("This Nasdaq dataset is not public. Enable public access in the admin panel.");
          setNasdaqData(null);
        } else {
          setNasdaqData(data);
        }
        setNasdaqLoading(false);
      })
      .catch((err) => {
        setNasdaqError(err.message);
        setNasdaqLoading(false);
      });
  }, [nasdaqMode, activeTab, nasdaqFilters]);

  function handleFilterChange(key, value) {
    setNasdaqFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handlePresetChange(preset) {
    setNasdaqFilters((prev) => ({
      ...prev,
      preset,
      from_date: preset ? '' : prev.from_date,
      to_date: preset ? '' : prev.to_date,
    }));
  }

  function resetFilters() {
    setNasdaqFilters({ ...DEFAULT_NASDAQ_FILTERS });
  }

  return (
    <section id="report" className="py-20 md:py-32 bg-ics-navy min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="section-divider mx-auto" />
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-ics-white mb-4">
            Performance Reports
          </h2>
          <p className="text-ics-gray max-w-2xl mx-auto">
            View public performance reports for our trading strategies.
          </p>
        </div>

        {/* Tab Selector */}
        <div className="hidden">
          <div className="inline-flex bg-white/5 rounded-xl p-1 border border-white/10">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                    activeTab === tab.id
                      ? "bg-ics-orange text-white shadow-lg"
                      : "text-ics-gray hover:text-ics-white hover:bg-white/5"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Signals Tab */}
        {activeTab === "signals" && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
            {/* Sidebar Controls & Filters */}
            <div className="lg:col-span-1 bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-md lg:sticky lg:top-6 space-y-6 shadow-xl">
              <div>
                <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-ics-orange" />
                  Select Analysis
                </h3>
                <Select value={selectedId} onValueChange={(val) => setSelectedId(val)}>
                  <SelectTrigger className="w-full bg-black/40 border-white/10 text-ics-white h-12 text-sm focus:ring-1 focus:ring-ics-orange/50 transition-all">
                    <SelectValue placeholder="Choose a report...">{selectedId ? signals.find(s => String(s.id) === selectedId)?.name : "Choose a report..."}</SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-ics-navy border-white/10 text-ics-white max-h-80">
                    {loading ? (
                      <div className="px-3 py-8 text-center text-ics-gray text-sm flex flex-col justify-center items-center gap-3">
                         <div className="w-6 h-6 border-2 border-ics-orange border-t-transparent rounded-full animate-spin" />
                         Loading analyses...
                      </div>
                    ) : signals.length === 0 ? (
                       <div className="px-3 py-8 text-center text-ics-gray text-sm">No reports found</div>
                    ) : (
                      signals.map((s) => (
                        <SelectItem key={s.id} value={String(s.id)} className="focus:bg-ics-orange/20 focus:text-ics-white cursor-pointer py-3 border-b border-white/5 last:border-0">
                          <div className="flex flex-col w-full gap-1.5">
                            <span className="truncate font-semibold text-[13px]">{s.name}</span>
                            <div className="flex justify-between items-center w-full gap-4">
                               <span className="text-[11px] text-ics-gray truncate">{s.dateRange || "All dates"}</span>
                               <StatusBadge status={s.status} />
                            </div>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Custom Date Range */}
              <div>
                <label className="block text-xs font-bold text-ics-gray uppercase tracking-wider mb-2">
                  Date Filters
                </label>
                <div className="space-y-2">
                  <input
                    type="date"
                    value={signalsFilters.from_date}
                    onChange={(e) => setSignalsFilters(prev => ({ ...prev, from_date: e.target.value }))}
                    className="w-full bg-black/40 border border-white/10 text-white rounded-lg px-3 py-2.5 text-xs focus:outline-none focus:border-ics-orange transition-all hover:bg-black/60"
                    title="From Date"
                  />
                  <input
                    type="date"
                    value={signalsFilters.to_date}
                    onChange={(e) => setSignalsFilters(prev => ({ ...prev, to_date: e.target.value }))}
                    className="w-full bg-black/40 border border-white/10 text-white rounded-lg px-3 py-2.5 text-xs focus:outline-none focus:border-ics-orange transition-all hover:bg-black/60"
                    title="To Date"
                  />
                </div>
                <button
                  onClick={() => setSignalsFilters({ from_date: '', to_date: '' })}
                  className="w-full py-2.5 rounded-xl text-xs font-bold bg-white/5 text-ics-gray hover:bg-white/10 hover:text-white border border-white/10 transition-all mt-4"
                >
                  Clear Dates
                </button>
              </div>
            </div>

            {/* Main Content Dashboard */}
            <div className="lg:col-span-3">
              {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm text-center flex items-center justify-center gap-2">
                  <Activity className="w-5 h-5" />
                  {error}
                </div>
              )}

              {(loading && !signals.length) ? (
                <div className="w-full h-96 flex flex-col items-center justify-center bg-white/5 border border-white/10 rounded-2xl animate-pulse shadow-xl">
                   <div className="w-12 h-12 border-4 border-ics-orange border-t-transparent rounded-full animate-spin mb-4" />
                   <p className="text-ics-gray font-medium">Loading Market Data...</p>
                </div>
              ) : (
                <>
                  <ReportDashboard data={reportData} loading={reportLoading} />

                  {!loading && signals.length === 0 && !selectedId && !error && (
                    <div className="text-center py-20 bg-white/5 border border-white/10 rounded-2xl mt-0 shadow-xl">
                      <Activity className="w-16 h-16 text-ics-gray/30 mx-auto mb-4" />
                      <p className="text-ics-white text-lg font-semibold mb-2">No Public Analysis Found</p>
                      <p className="text-ics-gray text-sm">There are currently no public analysis reports available for this market.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Nasdaq Tab */}
        {activeTab === "nasdaq" && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
            {/* Sidebar Controls & Filters */}
            <div className="lg:col-span-1 bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-md lg:sticky lg:top-6 space-y-6">
              <div>
                <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-400" />
                  Strategy Bot Mode
                </h3>
                <div className="grid grid-cols-2 gap-2 bg-black/20 p-1.5 rounded-xl border border-white/5">
                  {["hourly", "daily"].map((mode) => (
                    <button
                      key={mode}
                      onClick={() => setNasdaqMode(mode)}
                      className={`py-2 rounded-lg text-xs font-bold transition-all ${
                        nasdaqMode === mode
                          ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/20"
                          : "text-ics-gray hover:text-white hover:bg-white/5"
                      }`}
                    >
                      {mode === "hourly" ? "Hourly Bot" : "Daily Bot"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date Presets */}
              <div>
                <label className="block text-xs font-bold text-ics-gray uppercase tracking-wider mb-2">
                  Quick Date Presets
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PRESET_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handlePresetChange(opt.value)}
                      className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all border ${
                        nasdaqFilters.preset === opt.value
                          ? "bg-amber-500/20 text-amber-300 border-amber-500/40 shadow-sm"
                          : "bg-white/5 text-ics-gray border-white/5 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Date Range */}
              <div>
                <label className="block text-xs font-bold text-ics-gray uppercase tracking-wider mb-2">
                  Custom Dates
                </label>
                <div className="space-y-2">
                  <input
                    type="date"
                    value={nasdaqFilters.from_date}
                    onChange={(e) => {
                      handleFilterChange('from_date', e.target.value);
                      handleFilterChange('preset', '');
                    }}
                    className="w-full bg-black/30 border border-white/10 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-blue-500"
                  />
                  <input
                    type="date"
                    value={nasdaqFilters.to_date}
                    onChange={(e) => {
                      handleFilterChange('to_date', e.target.value);
                      handleFilterChange('preset', '');
                    }}
                    className="w-full bg-black/30 border border-white/10 text-white rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Filters: Ticker, Outcome, Stats Period */}
              <div className="space-y-3 pt-2 border-t border-white/10">
                <div>
                  <label className="block text-xs font-bold text-ics-gray uppercase tracking-wider mb-1">
                    Ticker Symbol
                  </label>
                  <input
                    type="text"
                    value={nasdaqFilters.ticker}
                    onChange={(e) => handleFilterChange('ticker', e.target.value)}
                    placeholder="Search ticker (e.g. QQQ)..."
                    className="w-full bg-black/30 border border-white/10 text-white rounded-lg px-3 py-2 text-xs placeholder:text-ics-gray/40 focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-ics-gray uppercase tracking-wider mb-1">
                    Trade Outcome
                  </label>
                  <select
                    value={nasdaqFilters.outcome}
                    onChange={(e) => handleFilterChange('outcome', e.target.value)}
                    className="w-full bg-black/30 border border-white/10 text-white rounded-lg px-3 py-2 text-xs appearance-none cursor-pointer"
                  >
                    {OUTCOME_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value} className="bg-slate-900 text-white">
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-ics-gray uppercase tracking-wider mb-1">
                    Period Grouping
                  </label>
                  <select
                    value={nasdaqFilters.stats_period}
                    onChange={(e) => handleFilterChange('stats_period', e.target.value)}
                    className="w-full bg-black/30 border border-white/10 text-white rounded-lg px-3 py-2 text-xs appearance-none cursor-pointer"
                  >
                    {STATS_PERIOD_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value} className="bg-slate-900 text-white">
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-ics-gray uppercase tracking-wider mb-1">
                    Rows Per Page
                  </label>
                  <select
                    value={nasdaqFilters.per_page}
                    onChange={(e) => handleFilterChange('per_page', Number(e.target.value))}
                    className="w-full bg-black/30 border border-white/10 text-white rounded-lg px-3 py-2 text-xs appearance-none cursor-pointer"
                  >
                    {PER_PAGE_OPTIONS.map((n) => (
                      <option key={n} value={n} className="bg-slate-900 text-white">
                        {n} rows per page
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={resetFilters}
                  className="w-full py-2.5 rounded-xl text-xs font-bold bg-white/5 text-ics-gray hover:bg-white/10 hover:text-white border border-white/10 transition-all mt-4"
                >
                  Reset All Filters
                </button>
              </div>
            </div>

            {/* Main Content Dashboard */}
            <div className="lg:col-span-3">
              {nasdaqError && (
                <div className="mb-6 p-6 bg-white/5 border border-white/10 rounded-2xl text-center">
                  <BarChart3 className="w-16 h-16 text-ics-gray/30 mx-auto mb-4" />
                  <p className="text-ics-white text-lg font-semibold mb-2">No Public Data Available</p>
                  <p className="text-ics-gray text-sm">{nasdaqError}</p>
                </div>
              )}

              {!nasdaqError && !nasdaqLoading && !nasdaqData && (
                <div className="text-center py-16">
                  <BarChart3 className="w-16 h-16 text-ics-gray/30 mx-auto mb-4" />
                  <p className="text-ics-white text-lg font-semibold mb-2">No Nasdaq Data</p>
                  <p className="text-ics-gray text-sm">Select a mode and filters to load data.</p>
                </div>
              )}

              {nasdaqData && (
                <NasdaqReportDashboard
                  data={nasdaqData}
                  loading={nasdaqLoading}
                  filters={nasdaqFilters}
                  onFilterChange={setNasdaqFilters}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
